import React, { useState, useEffect } from 'react';
import { Link, useNavigate } from 'react-router-dom';
import axios from 'axios';
import { useAuth } from '../contexts/AuthContext';
import { Star, Heart, ShoppingCart, Trash2 } from 'lucide-react';
import { toast } from 'sonner';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

const Wishlist = () => {
  const { user } = useAuth();
  const navigate = useNavigate();
  const [wishlist, setWishlist] = useState([]);
  const [loading, setLoading] = useState(true);

  useEffect(() => {
    if (!user || user === false) {
      navigate('/login');
      return;
    }
    fetchWishlist();
  }, [user]);

  const fetchWishlist = async () => {
    try {
      const { data } = await axios.get(`${API}/wishlist`, { withCredentials: true });
      setWishlist(data);
    } catch (error) {
      console.error('Error fetching wishlist:', error);
    } finally {
      setLoading(false);
    }
  };

  const removeFromWishlist = async (productId) => {
    try {
      await axios.delete(`${API}/wishlist/${productId}`, { withCredentials: true });
      toast.success('Retiré des favoris');
      fetchWishlist();
    } catch (error) {
      toast.error('Erreur lors de la suppression');
    }
  };

  const addToCart = async (productId) => {
    try {
      await axios.post(
        `${API}/cart`,
        { product_id: productId, quantity: 1 },
        { withCredentials: true }
      );
      toast.success('Ajouté au panier');
    } catch (error) {
      toast.error(error.response?.data?.detail || 'Erreur');
    }
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center">
        <div className="animate-spin rounded-full h-12 w-12 border-b-2 border-primary"></div>
      </div>
    );
  }

  return (
    <div className="min-h-screen bg-surface py-8">
      <div className="max-w-7xl mx-auto px-4 sm:px-6 lg:px-8">
        <div className="flex items-center gap-3 mb-8">
          <Heart className="w-8 h-8 text-brand-orange fill-brand-orange" />
          <h1 className="text-3xl font-bold" style={{ fontFamily: 'IBM Plex Sans' }}>
            Mes favoris
          </h1>
        </div>

        {wishlist.length === 0 ? (
          <div className="bg-white rounded-sm border border-border p-12 text-center">
            <Heart className="w-16 h-16 text-muted-foreground mx-auto mb-4" />
            <p className="text-muted-foreground mb-4">Votre liste de favoris est vide</p>
            <Link
              to="/products"
              className="inline-block btn-primary px-6 py-3 rounded-sm font-semibold"
              data-testid="browse-products-link"
            >
              Parcourir les produits
            </Link>
          </div>
        ) : (
          <div className="grid grid-cols-2 md:grid-cols-3 lg:grid-cols-4 gap-6">
            {wishlist.map((item) => (
              <div
                key={item.product_id}
                className="product-card bg-white border border-border rounded-sm overflow-hidden"
                data-testid={`wishlist-item-${item.product_id}`}
              >
                <Link to={`/products/${item.product_id}`}>
                  <div className="aspect-square bg-surface">
                    <img
                      src={item.product?.images?.[0]}
                      alt={item.product?.name}
                      className="w-full h-full object-cover"
                    />
                  </div>
                </Link>
                <div className="p-4">
                  <Link to={`/products/${item.product_id}`}>
                    <h3 className="font-semibold mb-2 line-clamp-2 hover:text-primary">
                      {item.product?.name}
                    </h3>
                  </Link>
                  <div className="flex items-center gap-2 mb-3">
                    <div className="flex items-center">
                      <Star className="w-4 h-4 fill-brand-orange text-brand-orange" />
                      <span className="text-sm ml-1">{item.product?.rating}</span>
                    </div>
                  </div>
                  <div className="text-xl font-bold text-primary mb-3">
                    {item.product?.price?.toFixed(2)} €
                  </div>
                  <div className="flex gap-2">
                    <button
                      onClick={() => addToCart(item.product_id)}
                      className="flex-1 btn-primary py-2 rounded-sm text-sm font-medium flex items-center justify-center gap-1"
                      data-testid={`add-cart-${item.product_id}`}
                    >
                      <ShoppingCart className="w-4 h-4" />
                      Ajouter
                    </button>
                    <button
                      onClick={() => removeFromWishlist(item.product_id)}
                      className="p-2 border border-border rounded-sm hover:border-destructive hover:text-destructive"
                      data-testid={`remove-wishlist-${item.product_id}`}
                    >
                      <Trash2 className="w-4 h-4" />
                    </button>
                  </div>
                </div>
              </div>
            ))}
          </div>
        )}
      </div>
    </div>
  );
};

export default Wishlist;
